import { EMPTY_STRING } from '@/constants';
import {
  TOA_GetFertilizationsEmbryos,
  TOA_GetFertilizationsIntEmbryos,
} from '@/rtk-query.gql';

import {
  DAY_6_5_FLAG,
  DAY_7_FLAG,
  GRADE_1,
  GRADE_1_LABEL,
  GRADE_2_LABEL,
  GRADE_2_SUFFIX,
  INITIAL_CANE_COUNT,
  INITIAL_COUNT,
  TOTALS_LABEL,
} from './constants';
import { CaneCount, CaneElement, EmbryoCounts } from './types';

// Moved to @/utils/freezeSites so Freeze V2 shares the same mapping table.
// Re-exported so existing Freeze imports keep working.
export {
  getFertilizationSiteId,
  getValidateFreezeSiteId,
} from '@/utils/freezeSites';

export const groupCanesByGrade = (canes: CaneCount[]) => {
  const grade1Canes = canes.filter(
    (cane) => !cane.currentcane.endsWith(GRADE_2_SUFFIX),
  );
  const grade2Canes = canes.filter((cane) =>
    cane.currentcane.endsWith(GRADE_2_SUFFIX),
  );

  return { grade1Canes, grade2Canes };
};

const createCaneElement = (caneName: string, count: number): CaneElement => ({
  currentCane: caneName,
  currentCaneCount: count,
});

const updateEmbryoCounts = (
  embryo: TOA_GetFertilizationsEmbryos | TOA_GetFertilizationsIntEmbryos,
  counts: EmbryoCounts,
): void => {
  const targetGrade = embryo.grade === GRADE_1 ? counts.grade1 : counts.grade2;

  if (embryo.d65 === DAY_6_5_FLAG) {
    targetGrade.d65Count++;
  } else if (embryo.d7 === DAY_7_FLAG) {
    targetGrade.d7Count++;
  }
};

const processEmbryoBatch = (
  embryos: TOA_GetFertilizationsEmbryos[] | TOA_GetFertilizationsIntEmbryos[],
  counts: EmbryoCounts,
): CaneElement[] => {
  const canes: CaneElement[] = [];
  let currentCane = EMPTY_STRING;
  let currentCaneCount = INITIAL_COUNT;

  for (const embryo of embryos) {
    if (currentCane === EMPTY_STRING) {
      currentCane = embryo.cane as string;
    }

    if (currentCane === embryo.cane) {
      currentCaneCount++;
    } else {
      canes.push(createCaneElement(currentCane, currentCaneCount));
      currentCane = embryo.cane as string;
      currentCaneCount = INITIAL_CANE_COUNT;
    }

    updateEmbryoCounts(embryo, counts);
  }

  if (currentCane !== EMPTY_STRING) {
    canes.push(createCaneElement(currentCane, currentCaneCount));
  }

  return canes;
};

const createSummaryData = (counts: EmbryoCounts) => {
  const { grade1, grade2 } = counts;
  const d65Total = grade1.d65Count + grade2.d65Count;
  const d7Total = grade1.d7Count + grade2.d7Count;
  const grade1Total = grade1.d65Count + grade1.d7Count;
  const grade2Total = grade2.d65Count + grade2.d7Count;
  const total = grade1Total + grade2Total;

  return [
    {
      category: GRADE_1_LABEL,
      d65: grade1.d65Count,
      d7: grade1.d7Count,
      totals: grade1Total,
    },
    {
      category: GRADE_2_LABEL,
      d65: grade2.d65Count,
      d7: grade2.d7Count,
      totals: grade2Total,
    },
    {
      category: TOTALS_LABEL,
      d65: d65Total,
      d7: d7Total,
      total,
    },
  ];
};

export const transformEmbryoData = (
  liveEmbryos: TOA_GetFertilizationsEmbryos[],
  tempEmbryos: TOA_GetFertilizationsIntEmbryos[],
) => {
  const counts: EmbryoCounts = {
    grade1: { d65Count: INITIAL_COUNT, d7Count: INITIAL_COUNT },
    grade2: { d65Count: INITIAL_COUNT, d7Count: INITIAL_COUNT },
  };

  const tempCanes = processEmbryoBatch(tempEmbryos, counts);
  const liveCanes = processEmbryoBatch(liveEmbryos, counts);
  const allCanes = [...tempCanes, ...liveCanes];
  const data = createSummaryData(counts);

  return { data, canes: allCanes };
};

export const areCanesCorrect = (
  embryos: TOA_GetFertilizationsIntEmbryos[],
  billAccountCaningOption: number,
): boolean => {
  for (const embryo of embryos) {
    const straw = (embryo as any)?.strawNumber ?? (embryo as any)?.straw;
    if (!straw) continue;

    const strawNum = Number(straw);
    if (isNaN(strawNum)) return false;

    let prefix = '';

    if (embryo.cane && embryo.cane.includes('-')) {
      const lastDashIndex = embryo.cane.lastIndexOf('-');
      prefix = embryo.cane.substring(0, lastDashIndex + 1);
    } else {
      prefix = embryo.cane || '';
    }

    let expectedSuffix = '';
    if (billAccountCaningOption === 100000001) {
      const group = Math.floor((strawNum - 1) / 3);
      const caneNumber = Math.floor((strawNum - 1) / 9 + 1).toString();
      expectedSuffix =
        caneNumber + String.fromCharCode('A'.charCodeAt(0) + (group % 3));
    } else if (billAccountCaningOption === 100000002) {
      const caneGroup = Math.floor((strawNum - 1) / 8 + 1);
      expectedSuffix = caneGroup.toString() + (strawNum % 2 === 1 ? 'A' : 'B');
    }

    const expectedCane = prefix + expectedSuffix;

    if (embryo.cane !== expectedCane) {
      return false;
    }
  }
  return true;
};
